// F2b GraphQL operations: backtests, analytics, and alerts.
// These are typed manually because codegen cannot run until the intelligence /
// analytics-service schemas are live in graphql-bff.
//
// Shape mirrors what graphql-codegen client-preset would emit. When the backends
// ship, delete this file, add the operations to queries.ts, and run `npm run codegen`.

import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { parse } from "graphql";

// ---------------------------------------------------------------------------
// Shared scalar types
// ---------------------------------------------------------------------------

export type AlertSeverity = "info" | "warning" | "critical";

// NOTE: the strategy-backtest documents (Backtests / BacktestRun /
// LaunchBacktest) were removed when the /trade/backtests page was switched to the
// blind walk-forward backtests in queriesBacktest.ts. The analytics-service
// strategy-backtest backend (bff: backtests / backtestRun / launchBacktest /
// cancelBacktest) is still live for a future strategy-backtest surface.
// The old ModelMetrics / ForecastVsActual documents also lived here but targeted
// operations that never existed on the bff; the real accuracy operations
// (ModelMape, ForecastVsActual, FeatureImportance, ModelVersions) live in
// queriesTrade.ts against the live schema.

// ---------------------------------------------------------------------------
// Alerts — rules CRUD + active feed (analytics-service rules engine via bff)
// ---------------------------------------------------------------------------

export interface AlertRule {
  id: string;
  name: string;
  metric: string | null;
  op: string | null;
  threshold: number | null;
  severity: AlertSeverity;
  enabled: boolean;
  assetId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AlertRulesQuery {
  alertRules: AlertRule[];
}
export interface AlertRulesQueryVariables { [key: string]: never }

export const AlertRulesDocument: TypedDocumentNode<
  AlertRulesQuery,
  AlertRulesQueryVariables
> = parse(/* GraphQL */ `
  query AlertRules {
    alertRules {
      id
      name
      metric
      op
      threshold
      severity
      enabled
      assetId
      createdAt
      updatedAt
    }
  }
`) as TypedDocumentNode<AlertRulesQuery, AlertRulesQueryVariables>;

export interface AlertConditionInput {
  metric: string;
  op: string;
  threshold: number;
  assetId?: number | null;
}
export interface CreateAlertRuleInput {
  name: string;
  condition: AlertConditionInput;
  severity: AlertSeverity;
  enabled?: boolean;
}
export interface CreateAlertRuleMutation {
  createAlertRule: AlertRule;
}
export interface CreateAlertRuleMutationVariables {
  input: CreateAlertRuleInput;
}

export const CreateAlertRuleDocument: TypedDocumentNode<
  CreateAlertRuleMutation,
  CreateAlertRuleMutationVariables
> = parse(/* GraphQL */ `
  mutation CreateAlertRule($input: AlertRuleInput!) {
    createAlertRule(input: $input) {
      id
      name
      metric
      op
      threshold
      severity
      enabled
      assetId
      createdAt
      updatedAt
    }
  }
`) as TypedDocumentNode<CreateAlertRuleMutation, CreateAlertRuleMutationVariables>;

export interface DeleteAlertRuleMutation {
  deleteAlertRule: { id: string };
}
export interface DeleteAlertRuleMutationVariables {
  id: string;
}

export const DeleteAlertRuleDocument: TypedDocumentNode<
  DeleteAlertRuleMutation,
  DeleteAlertRuleMutationVariables
> = parse(/* GraphQL */ `
  mutation DeleteAlertRule($id: ID!) {
    deleteAlertRule(id: $id) {
      id
    }
  }
`) as TypedDocumentNode<DeleteAlertRuleMutation, DeleteAlertRuleMutationVariables>;

export interface AckAlertMutation {
  ackAlert: { id: string; status: string; ackedAt: string | null };
}
export interface AckAlertMutationVariables {
  id: string;
}

export const AckAlertDocument: TypedDocumentNode<
  AckAlertMutation,
  AckAlertMutationVariables
> = parse(/* GraphQL */ `
  mutation AckAlert($id: ID!) {
    ackAlert(id: $id) {
      id
      status
      ackedAt
    }
  }
`) as TypedDocumentNode<AckAlertMutation, AckAlertMutationVariables>;

// ---------------------------------------------------------------------------
// OT telemetry — latest cell/rack snapshots for the Telemetry page
// ---------------------------------------------------------------------------

export interface BmsCellMetric {
  time: string;
  rackId: number;
  moduleId: number;
  cellId: number;
  voltageV: number | null;
  temperatureC: number | null;
}
export interface BmsCellsQuery {
  bmsCells: BmsCellMetric[];
}
export interface BmsCellsQueryVariables {
  assetId: string;
}

export const BmsCellsDocument: TypedDocumentNode<
  BmsCellsQuery,
  BmsCellsQueryVariables
> = parse(/* GraphQL */ `
  query BmsCells($assetId: ID!) {
    bmsCells(assetId: $assetId) {
      time
      rackId
      moduleId
      cellId
      voltageV
      temperatureC
    }
  }
`) as TypedDocumentNode<BmsCellsQuery, BmsCellsQueryVariables>;

export interface BmsRackMetric {
  time: string;
  rackId: number;
  currentA: number | null;
  socPct: number | null;
  sohPct: number | null;
  voltageV: number | null;
  temperatureMinC: number | null;
  temperatureMaxC: number | null;
}
export interface BmsRacksQuery {
  bmsRacks: BmsRackMetric[];
}
export interface BmsRacksQueryVariables {
  assetId: string;
}

export const BmsRacksDocument: TypedDocumentNode<
  BmsRacksQuery,
  BmsRacksQueryVariables
> = parse(/* GraphQL */ `
  query BmsRacks($assetId: ID!) {
    bmsRacks(assetId: $assetId) {
      time
      rackId
      currentA
      socPct
      sohPct
      voltageV
      temperatureMinC
      temperatureMaxC
    }
  }
`) as TypedDocumentNode<BmsRacksQuery, BmsRacksQueryVariables>;

export interface PcsState {
  assetId: number;
  acActivePowerKw: number | null;
  acReactivePowerKvar: number | null;
  dcVoltageV: number | null;
  gridFrequencyHz: number | null;
  powerFactor: number | null;
  igbtTempC: number | null;
  internalTempC: number | null;
  energyChargedKwh: number | null;
  energyDischargedKwh: number | null;
  operationalMode: string | null;
  updatedAt: string | null;
}
export interface PcsStateQuery {
  pcsState: PcsState | null;
}
export interface PcsStateQueryVariables {
  assetId: string;
}

export const PcsStateDocument: TypedDocumentNode<
  PcsStateQuery,
  PcsStateQueryVariables
> = parse(/* GraphQL */ `
  query PcsState($assetId: ID!) {
    pcsState(assetId: $assetId) {
      assetId
      acActivePowerKw
      acReactivePowerKvar
      dcVoltageV
      gridFrequencyHz
      powerFactor
      igbtTempC
      internalTempC
      energyChargedKwh
      energyDischargedKwh
      operationalMode
      updatedAt
    }
  }
`) as TypedDocumentNode<PcsStateQuery, PcsStateQueryVariables>;

export interface EmsState {
  assetId: number;
  activePowerSetpointKw: number | null;
  reactivePowerSetpointKvar: number | null;
  rampRateKwPerS: number | null;
  dispatchMode: string | null;
  availabilityPct: number | null;
  roundTripEfficiencyPct: number | null;
  updatedAt: string | null;
}
export interface EmsStateQuery {
  emsState: EmsState | null;
}
export interface EmsStateQueryVariables {
  assetId: string;
}

export const EmsStateDocument: TypedDocumentNode<
  EmsStateQuery,
  EmsStateQueryVariables
> = parse(/* GraphQL */ `
  query EmsState($assetId: ID!) {
    emsState(assetId: $assetId) {
      assetId
      activePowerSetpointKw
      reactivePowerSetpointKvar
      rampRateKwPerS
      dispatchMode
      availabilityPct
      roundTripEfficiencyPct
      updatedAt
    }
  }
`) as TypedDocumentNode<EmsStateQuery, EmsStateQueryVariables>;

// ---------------------------------------------------------------------------
// Settlement (SEM meter blocks) + connector fleet health
// ---------------------------------------------------------------------------

export interface MeterReading {
  meterId: string;
  time: string;
  tradingDate: string;
  intervalNumber: number;
  intervalMinutes: number;
  revision: string;
  activeImportKwh: number | null;
  activeExportKwh: number | null;
  reactiveImportKvarh: number | null;
  reactiveExportKvarh: number | null;
  meterClass: string | null;
}
export interface MeterReadingsQuery {
  meterReadings: MeterReading[];
}
export interface MeterReadingsQueryVariables {
  assetId: string;
  tradingDate: string;
  meterClass?: string | null;
}

export const MeterReadingsDocument: TypedDocumentNode<
  MeterReadingsQuery,
  MeterReadingsQueryVariables
> = parse(/* GraphQL */ `
  query MeterReadings($assetId: ID!, $tradingDate: String!, $meterClass: String) {
    meterReadings(assetId: $assetId, tradingDate: $tradingDate, meterClass: $meterClass) {
      meterId
      time
      tradingDate
      intervalNumber
      intervalMinutes
      revision
      activeImportKwh
      activeExportKwh
      reactiveImportKvarh
      reactiveExportKvarh
      meterClass
    }
  }
`) as TypedDocumentNode<MeterReadingsQuery, MeterReadingsQueryVariables>;

export interface ConnectorStatusRow {
  siteId: string;
  lastSeenAt: string | null;
  agentVersion: string | null;
  bufferDepth: number | null;
  lagSeconds: number | null;
}
export interface ConnectorStatusQuery {
  connectorStatus: ConnectorStatusRow[];
}
export interface ConnectorStatusQueryVariables { [key: string]: never }

export const ConnectorStatusDocument: TypedDocumentNode<
  ConnectorStatusQuery,
  ConnectorStatusQueryVariables
> = parse(/* GraphQL */ `
  query ConnectorStatus {
    connectorStatus {
      siteId
      lastSeenAt
      agentVersion
      bufferDepth
      lagSeconds
    }
  }
`) as TypedDocumentNode<ConnectorStatusQuery, ConnectorStatusQueryVariables>;

export interface ResolveAlertMutation {
  resolveAlert: { id: string; status: string; resolvedAt: string | null };
}
export interface ResolveAlertMutationVariables {
  id: string;
}

export const ResolveAlertDocument: TypedDocumentNode<
  ResolveAlertMutation,
  ResolveAlertMutationVariables
> = parse(/* GraphQL */ `
  mutation ResolveAlert($id: ID!) {
    resolveAlert(id: $id) {
      id
      status
      resolvedAt
    }
  }
`) as TypedDocumentNode<ResolveAlertMutation, ResolveAlertMutationVariables>;
